import {
  CardSubWrapper,
  CardWrapper,
  HeadingWrapper,
} from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import React, { useState } from 'react';
import styled from 'styled-components';
import ShowRidereTeamCell from 'app/components/CustomDataGrid/Cells/ShowRidereTeamCell';
import ShowRiderCell from 'app/components/CustomDataGrid/Cells/ShowRiderCell';
import ShowRiderBackNumberCell from 'app/components/CustomDataGrid/Cells/ShowRiderBackNumberCell';
import ShowRiderStateCell from 'app/components/CustomDataGrid/Cells/ShowRiderStateCell';
import ShowRiderGradeCell from 'app/components/CustomDataGrid/Cells/ShowRiderGradeCell';
import { GroupedShowRiderListWrapper, RiderSplitClass } from 'services/openapi';
import ManageRiderClassSplitUpdateCell from 'app/components/CustomDataGrid/Cells/ManageRiderClassSplitUpdateCell';
import { usePermission } from 'hooks/usePermission';
import { TitleCardWrapper } from 'admin/pages/ShowsPage/ShowResultPage/ScoreBoardPage';
import { PageMainLabel } from 'app/components/SHLabel';
import { CommonCell } from 'admin/pages/ShowsPage/ShowResultPage/ElementaryPage/Cells/CommonCell';
import NoDataFound from 'app/components/NoDataFound';
import { convertCmToFeetInches } from 'utils/common';
type Props = {
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  debounceShowSearch: string;
  fetchManageGetShowRiderList: (
    showId: string,
    teamFilter?: string[] | undefined,
    categoryId?: string[] | undefined,
    divisionId?: string[] | undefined,
    searchInput?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  showRiderDataList?: GroupedShowRiderListWrapper | undefined;
  deleteShowRider: (
    riderId?: string | undefined,
    showId?: string | undefined,
    teamFilter?: string[] | undefined,
    categoryId?: string[] | undefined,
    divisionId?: string[] | undefined,
    searchInput?: string | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  teamId: string[];
  onSaveClick: (requestBody: Array<RiderSplitClass>) => void;
  onClassUncheck?: (item: RiderSplitClass) => void;
  onClassDelete?: (showRiderIds: string[]) => void;
  divisionIdsArray?: string[];
  categoryIdsArray?: string[];
};

const ShowManageRiderTable = ({
  setPageSkip,
  pageSkip,
  debounceShowSearch,
  fetchManageGetShowRiderList,
  showRiderDataList,
  deleteShowRider,
  teamId,
  onSaveClick,
  onClassUncheck,
  onClassDelete,
  divisionIdsArray,
  categoryIdsArray,
}: Props) => {
  const { hasViewPermission } = usePermission();
  const [showAdditionalDetails, setShowAdditionalDetails] = useState(false);

  const onClassACheckboxChange = (e, event, className) => {};

  const toggleAdditionalDetails = () => {
    setShowAdditionalDetails(!showAdditionalDetails);
  };

  const getRiderClassStats = (riders: any[] | undefined) => {
    if (!riders || riders.length === 0) {
      return { totalRiders: 0, railRiders: 0, patternRiders: 0 };
    }

    const riderSet = new Set();
    const railSet = new Set();
    const patternSet = new Set();

    riders.forEach(rider => {
      riderSet.add(rider.id);
      rider.class?.forEach(classItem => {
        if (classItem.riderClass?.name.includes('Rail')) {
          railSet.add(rider.id);
        } else if (classItem.riderClass?.name.includes('Pattern')) {
          patternSet.add(rider.id);
        }
      });
    });

    return {
      totalRiders: riderSet.size,
      railRiders: railSet.size,
      patternRiders: patternSet.size,
    };
  };

  const ShowRiderList = [
    {
      field: 'team',
      title: 'Team',
      cell: e => <ShowRidereTeamCell e={e} />,
      width: '200',
      locked: true,
    },
    {
      field: 'rider',
      title: 'Rider',
      cell: e => <ShowRiderCell e={e} />,
      width: '200',
    },
    ...(showAdditionalDetails
      ? [
          {
            field: 'grade',
            title: 'Grade',
            cell: e => <ShowRiderGradeCell e={e} />,
            width: '80',
          },
          {
            field: 'height',
            title: 'Height',
            cell: e => (
              <CommonCell
                value={
                  e?.dataItem?.height
                    ? convertCmToFeetInches(e.dataItem.height)
                    : '-'
                }
              />
            ),
            width: '80',
          },
          {
            field: 'weight',
            title: 'Weight',
            cell: e => (
              <CommonCell
                value={e?.dataItem?.weight ? `${e.dataItem.weight} lbs` : '-'}
              />
            ),
            width: '80',
          },
          {
            field: 'state',
            title: 'State',
            cell: e => <ShowRiderStateCell e={e} />,
            width: '120',
          },
        ]
      : []),
    {
      field: 'backNumber',
      title: 'Back',
      cell: e => <ShowRiderBackNumberCell e={e} />,
      width: '100',
    },
    ...(hasViewPermission('0500101009')
      ? [
          {
            field: 'class',
            title: 'Class',
            cell: e => (
              <ManageRiderClassSplitUpdateCell
                e={e}
                onClassACheckboxChange={onClassACheckboxChange}
                onSaveClick={onSaveClick}
                onClassUncheck={onClassUncheck}
                onClassDelete={onClassDelete}
              />
            ),
            width: '500',
          },
        ]
      : []),
  ];

  return (
    <>
      <CardWrapper style={{ paddingTop: '15px' }}>
        <CardSubWrapper className="d-flex">
          {showRiderDataList?.groupedRiders?.length! > 0 && (
            <AdditionalDetailsWrapper>
              <AdditionalDetailsButton onClick={toggleAdditionalDetails}>
                Show Additional Details
              </AdditionalDetailsButton>
              <ToggleButton
                onClick={toggleAdditionalDetails}
                isExpanded={showAdditionalDetails}
              >
                {showAdditionalDetails ? '−' : '+'}
              </ToggleButton>
            </AdditionalDetailsWrapper>
          )}
        </CardSubWrapper>
        <GridWrapper>
          {showRiderDataList?.groupedRiders?.map((item, index) => (
            <div key={index}>
              <TitleCardWrapper className="title-card-wrapper">
                <HeadingWrapper className="heading-wrapper">
                  <PageMainLabel className="label-wrapper">
                    {item.category?.name} {item.division?.name} - Total Riders:
                    ({item.riders?.length}) | Rail Riders: (
                    {getRiderClassStats(item?.riders as any).railRiders}) |
                    Pattern Riders: (
                    {getRiderClassStats(item?.riders as any).patternRiders})
                  </PageMainLabel>
                </HeadingWrapper>
              </TitleCardWrapper>
              <CustomDataGrid
                data={item?.riders ?? []}
                columns={ShowRiderList}
              />
            </div>
          ))}
          {showRiderDataList?.groupedRiders?.length === 0 && <NoDataFound />}
        </GridWrapper>
      </CardWrapper>
    </>
  );
};

export default ShowManageRiderTable;
const GridWrapper = styled.div`
  .k-grid-content tr {
    .k-grid-content-sticky {
      :hover {
      }
    }
  }
  .k-grid-container {
    height: auto !important;
  }
  .k-grid-container .k-grid-content {
    height: auto !important;
    max-height: none !important;
  }
  .k-grid {
    height: auto !important;
  }
  .k-grid-content {
    min-height: 0 !important;
  }
`;
const AdditionalDetailsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const AdditionalDetailsButton = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.primaryColor};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: ${p => p.theme.primaryHoverColor};
  }
`;

const ToggleButton = styled.button<{ isExpanded: boolean }>`
  width: 34px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${p => (p.isExpanded ? '#4764FF' : p.theme.borderColor)};
  background: ${p => (p.isExpanded ? '#4764FF' : p.theme.buttonBackground)};
  color: ${p => (p.isExpanded ? '#ffffff' : '#888888')};
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.25s ease-in-out;

  &:hover {
    border-color: #4764ff;
    background: ${p => (p.isExpanded ? '#3651d4' : '#f5f7ff')};
  }

  &:active {
    transform: scale(0.95);
  }
`;
